import { Component } from "@theme/component";

const GROUPS = ["location", "age", "day", "frequency", "available"];

class KurzyFilterComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.cards = Array.from(this.querySelectorAll("[data-kurz-card]"));
    this.inputs = Array.from(this.querySelectorAll("input[data-group]:not([data-all])"));
    this.resetInputs = Array.from(this.querySelectorAll("input[data-group][data-all]"));

    this.initialLimit = Number(this.dataset.initial) || 0;
    this.step = Number(this.dataset.step) || this.initialLimit;
    this.limit = this.initialLimit || Infinity;

    this.#readUrl();
    this.#apply({ updateUrl: false });

    this.classList.add("is-ready");
  }

  onChange(event) {
    const input = event.target;

    if (input instanceof HTMLInputElement && input.hasAttribute("data-all")) {
      for (const other of this.inputs) {
        if (other.dataset.group === input.dataset.group) other.checked = false;
      }
    }

    this.#applyBusy();
  }

  onSelectAll(event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) return;

    input.checked = true;

    for (const other of this.inputs) {
      if (other.dataset.group === input.dataset.group) other.checked = false;
    }

    this.#applyBusy();
  }

  onReset(event) {
    event.preventDefault();

    for (const input of this.inputs) input.checked = false;

    this.#applyBusy();
  }

  onShowMore(event) {
    event.preventDefault();

    this.limit += this.step || this.cards.length;
    this.#apply({ updateUrl: false });
  }

  onShowAll(event) {
    event.preventDefault();

    this.limit = Infinity;
    this.#apply({ updateUrl: false });
  }

  #applyBusy() {
    this.limit = this.initialLimit || Infinity;
    this.classList.add("is-busy");
    clearTimeout(this.busyTimer);

    requestAnimationFrame(() => {
      this.#apply({ updateUrl: true });
      this.busyTimer = setTimeout(() => this.classList.remove("is-busy"), 250);
    });
  }

  get #selected() {
    const selected = {};

    for (const group of GROUPS) selected[group] = new Set();

    for (const input of this.inputs) {
      if (input.checked) selected[input.dataset.group]?.add(input.value);
    }

    return selected;
  }

  #cardValues(card, group) {
    return (card.dataset[group] || "").split(",").filter(Boolean);
  }

  #matches(card, selected, skipGroup) {
    for (const group of GROUPS) {
      if (group === skipGroup) continue;

      const active = selected[group];

      if (!active || active.size === 0) continue;

      const values = this.#cardValues(card, group);

      if (!values.some((value) => active.has(value))) return false;
    }

    return true;
  }

  #apply({ updateUrl }) {
    const selected = this.#selected;
    let visible = 0;
    let shown = 0;

    for (const card of this.cards) {
      const matches = this.#matches(card, selected, null);
      const wrapper = card.closest(".kurzy__card-wrapper") || card;
      const display = matches && visible < this.limit;

      wrapper.classList.toggle("is-hidden", !display);

      if (matches) visible += 1;
      if (display) shown += 1;
    }

    for (const input of this.inputs) {
      const group = input.dataset.group;
      const count = this.cards.filter(
        (card) => this.#matches(card, selected, group) && this.#cardValues(card, group).includes(input.value)
      ).length;

      const chip = input.closest(".kurzy-chip");
      const counter = chip?.querySelector("[data-count]");

      if (counter) counter.textContent = String(count);

      input.disabled = count === 0 && !input.checked;
      chip?.classList.toggle("is-disabled", input.disabled);
    }

    for (const input of this.resetInputs) {
      input.checked = selected[input.dataset.group]?.size === 0;
    }

    this.#renderSummary(visible, shown);

    if (updateUrl) this.#writeUrl(selected);
  }

  #renderSummary(visible, shown) {
    const { empty, count, reset, more, showing, showMore, showAll } = this.refs;

    if (empty) empty.hidden = visible !== 0;
    if (reset) reset.hidden = !this.inputs.some((input) => input.checked);

    if (count) {
      const { labelOne, labelFew, labelMany } = this.dataset;
      let label = labelMany;

      if (visible === 1) label = labelOne;
      else if (visible >= 2 && visible <= 4) label = labelFew;

      count.textContent = `${visible} ${label || ""}`.trim();
    }

    const remaining = visible - shown;

    if (more) more.hidden = remaining <= 0;
    if (showMore) showMore.hidden = remaining <= 0;
    if (showAll) showAll.hidden = remaining <= 0 || remaining <= this.step;

    if (showing) {
      showing.textContent = (this.dataset.labelShowing || "")
        .replace("{shown}", String(shown))
        .replace("{total}", String(visible));
    }
  }

  #readUrl() {
    const params = new URLSearchParams(window.location.search);

    for (const input of this.inputs) {
      const values = params.get(input.dataset.group);

      if (!values) continue;

      if (values.split(",").includes(input.value)) input.checked = true;
    }
  }

  #writeUrl(selected) {
    const params = new URLSearchParams(window.location.search);

    for (const group of GROUPS) {
      const values = Array.from(selected[group]);

      if (values.length) params.set(group, values.join(","));
      else params.delete(group);
    }

    const query = params.toString();

    window.history.replaceState({}, "", query ? `${window.location.pathname}?${query}` : window.location.pathname);
  }
}

if (!customElements.get("kurzy-filter-component")) {
  customElements.define("kurzy-filter-component", KurzyFilterComponent);
}
